import React, { useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router';

import { Tooltip } from '@spark-labs/avocado-ui';
import { camelToSentenceCase, truncateWords } from '@go-shared/lib/string';
import {
  exportEventTemplatesToCSV,
  paginateEventTemplates,
  setArchived,
  update,
  bare,
} from '@go-client/lib/api/eventTemplate';
import { BareEventTemplate, EventTemplate } from '@go-shared/types/eventTemplate';
import Config from '@go-client/lib/config';

import './EventLibrary.scss';
import { StaffTemplate } from '@go-shared/types/staffTemplate';
import {
  getStaffTemplatesByPropertyId,
  populate as populateStaffTemplate,
} from '@go-client/lib/api/staffTemplate';
import { CopyToClipboardButton } from '@go-client/components/CopyToClipboard';
import {
  BubbleBadge,
  BubbleButton,
  BubbleCheckbox,
  BubbleDebounce,
  BubbleDrawer,
  BubbleFormGroup,
  BubbleInput,
  BubbleOverlay,
  BubbleTable,
  BubbleAccordion,
} from '@go-client/components/BubbleUI';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { hashStringToColor } from '@go-shared/lib/utils';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import Libraries from './Libraries';
import { canManageLibraries, canModifyMultiPropertyItem } from '@go-shared/lib/permissions';
import { LibrariesSubNavigation } from '@go-client/components/Navigations/LibrariesSubNavigation';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from '@go-client/components/BubbleUI/BubbleTable';
import { SortOrder } from '@go-shared/types/userActionRecord';
import { PaginationMetadata, DEFAULT_PER_PAGE } from '@go-shared/types/pagination';
import { BubbleTablePaginationFooter } from '@go-client/components/BubbleUI/BubbleTablePaginationFooter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getLocaleDateTimeStringUTC } from '@go-shared/lib/time';
import { getSortingIcon } from '@go-shared/lib/paginations';
import AccessGlanceButton from '@go-client/components/AccessGlanceButton';
import { McmEndpoints } from '@go-client/components/Mcm/McmEndpoints';
import { readQs } from '@go-shared/lib/querystring';
import BubbleDrawerBasicContainer from '@go-client/components/BubbleUI/BubbleDrawerHeader';
import PropertyAccessFormik from '@go-client/components/Formik/PropertyAccessFormik';
import { BrandAttribute, BrandLevel, BrandSegment } from '@go-shared/types/brand';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import EventTemplateFormik, {
  EventTemplateFormikMode,
} from '@go-client/components/Formik/EventTemplateFormik';
import { isReservablePopulatedEvent } from '@go-shared/lib/reserve';
import { notUndefined } from '@go-shared/lib/functional';

type EventLibraryPageProps = RouteComponentProps;

interface EventLibraryDataBundle {
  tableItems: EventTableItem[];
  staffTemplates: StaffTemplate[];
}

type EventTableItem = {
  id: string;
  title: string;
  description: string;
  segment?: BrandSegment;
  images: number;
  levels: BrandLevel[];
  isReservable: boolean;
  attributes: BrandAttribute[];
  duration: string;
  isPrivate: boolean;
  crewOnly: boolean;
  inventoried: boolean;
  simple: boolean;
  createdAt?: string;
  _source: EventTemplate;
};

type VisibleFields = {
  [key: string]: boolean;
};

type EventsTableFields = BubbleTableFields<EventTableItem> & {
  _visible?: boolean;
  _toggleable?: boolean;
};

type PaginationParams<T> = {
  page: number;
  perPage: number;
  sortBy: keyof T;
  sortDirection: SortOrder;
  archived: boolean;
  startDate?: string;
  endDate?: string;
  search?: string;
  propertyId?: string;
};

export default function EventLibrary(props: EventLibraryPageProps) {
  const { ...routeProps } = props;

  const { user } = useUserInfo();
  const { activeProperty: property } = useActiveProperty();
  const serverInfo = useServerInfo();

  const [loading, setLoading] = useState(true);
  const [dataBundle, setDataBundle] = useState<EventLibraryDataBundle | undefined>(undefined);
  const [paginationMetadata, setPaginationMetadata] =
    useState<PaginationMetadata | undefined>(undefined);

  const [mode, setMode] = useState<EventTemplateFormikMode | undefined>(undefined);
  const [showAccessForm, setShowAccessForm] = useState<BareEventTemplate | undefined>(undefined);
  const [showDelete, setShowDelete] = useState<{ [key: string]: boolean }>({});

  const [visibleFields, setVisibleFields] = useState<VisibleFields>({
    access: true,
    title: true,
    description: true,
    segment: true,
    levels: false,
    attributes: false,
    private: true,
    crew: true,
    simple: false,
    duration: false,
    inventoried: false,
    images: true,
    createdAt: false,
    reservable: true,
    actions: true,
    endpoints: true,
  });

  const queries = readQs();

  const [paginationParams, setPaginationParams] = useState<PaginationParams<BareEventTemplate>>({
    page: 1,
    perPage: DEFAULT_PER_PAGE,
    sortBy: 'createdAt',
    sortDirection: SortOrder.DESC,
    startDate: '',
    endDate: '',
    archived: false,
    propertyId: property._id,
    search: typeof queries.search === 'string' ? queries.search : '',
  });

  const contentRef = useRef<HTMLDivElement | null>(null);
  // this will scroll the page to top when search is modified
  useEffect(() => {
    // We want to scroll to top only when we are in page 1 or paginationParams are reset
    if (paginationParams.page !== 1) {
      return;
    }

    if (contentRef && contentRef.current) contentRef.current.scrollTop = 0;
  }, [paginationParams, contentRef]);

  useEffect(() => {
    if (property._id !== paginationParams.propertyId) {
      setPaginationParams({ ...paginationParams, page: 1, propertyId: property._id });
    }
  }, [property]);

  async function refresh() {
    setShowAccessForm(undefined);
    setLoading(true);
    await fetchDataBundle();
  }

  useEffect(() => {
    refresh();
  }, [paginationParams]);

  async function fetchDataBundle() {
    setLoading(true);
    try {
      const [bareStaffTemplates, eventTemplates] = await Promise.all([
        getStaffTemplatesByPropertyId(property._id),
        paginateEventTemplates({
          page: paginationParams.page,
          perPage: paginationParams.perPage,
          sortBy: paginationParams.sortBy,
          sortDirection: paginationParams.sortDirection,
          propertyId: property._id,
          ...(paginationParams.search && { search: paginationParams.search }),
          ...(paginationParams.archived && { archived: paginationParams.archived }),
          ...(paginationParams.startDate && { startDate: paginationParams.startDate }),
          ...(paginationParams.endDate && { endDate: paginationParams.endDate }),
        }),
      ]);

      let staffTemplates = bareStaffTemplates.map(st => populateStaffTemplate(st, property));

      setPaginationMetadata(eventTemplates.meta);

      const tableItems: EventTableItem[] = eventTemplates.data.map(et => {
        return {
          id: et._id,
          title: et.title,
          description: truncateWords(et.description, Config.EVENT_DESCRIPTION_MAX_LENGTH),
          segment: et.segment,
          images: et.images.length,
          levels: et.levels,
          attributes: et.attributes,
          duration: et.defaultDuration.toString(),
          isReservable: isReservablePopulatedEvent(et),
          isPrivate: et.isPrivate,
          crewOnly: !!et.crewOnly,
          inventoried: !!et.inventoried,
          simple: !!et.simple,
          createdAt: et.createdAt,
          _source: et,
        };
      });

      setDataBundle({
        staffTemplates,
        tableItems:
          paginationParams.page > 1
            ? [...(dataBundle?.tableItems ?? []), ...tableItems]
            : tableItems,
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit() {
    setShowAccessForm(undefined);
    setMode(undefined);
    refresh();
  }

  async function handleArchive(item: EventTemplate, state: boolean) {
    await setArchived(
      bare({
        ...item,
        archived: state,
      })
    );

    setDataBundle({
      ...dataBundle,
      tableItems: (dataBundle?.tableItems || []).filter(et => et._source._id !== item._id),
      staffTemplates: dataBundle?.staffTemplates || [],
    });
  }

  async function generateCSV() {
    const result = await exportEventTemplatesToCSV(property._id);
    if (result.location) {
      window.open(result.location, '_blank');
    }
  }

  const tableFields: Array<EventsTableFields> = [
    { key: 'access', behavior: BubbleTableFieldBehavior.Condense },
    {
      key: 'title',
      tdProps: { style: { minWidth: '200px' } },
      _toggleable: true,
    },
    {
      key: 'description',
      tdProps: { style: { minWidth: '400px' } },
      _toggleable: true,
    },
    {
      key: 'duration',
      behavior: BubbleTableFieldBehavior.Condense,
      _toggleable: true,
    },
    {
      key: 'segment',
      behavior: BubbleTableFieldBehavior.Condense,
      _toggleable: true,
    },
    {
      key: 'levels',
      behavior: BubbleTableFieldBehavior.Condense,
      _toggleable: true,
    },
    {
      key: 'attributes',
      behavior: BubbleTableFieldBehavior.Condense,
      _toggleable: true,
    },
    {
      key: 'endpoints',
      behavior: BubbleTableFieldBehavior.Condense,
      thProps: { className: 'text-center' },
      tdProps: { className: 'text-center' },
      _toggleable: true,
    },
    {
      key: 'private',
      behavior: BubbleTableFieldBehavior.Condense,
      thProps: { className: 'text-center' },
      tdProps: { className: 'text-center' },
      _toggleable: true,
    },
    {
      key: 'crew',
      behavior: BubbleTableFieldBehavior.Condense,
      thProps: { className: 'text-center' },
      tdProps: { className: 'text-center' },
      _toggleable: true,
    },
    {
      key: 'simple',
      behavior: BubbleTableFieldBehavior.Condense,
      thProps: { className: 'text-center' },
      tdProps: { className: 'text-center' },
      _toggleable: true,
    },
    {
      key: 'inventoried',
      behavior: BubbleTableFieldBehavior.Condense,
      thProps: { className: 'text-center' },
      tdProps: { className: 'text-center' },
      _toggleable: true,
    },
    {
      key: 'images',
      behavior: BubbleTableFieldBehavior.MinWidth,
      thProps: { className: 'text-center' },
      tdProps: { className: 'text-center' },
      _toggleable: true,
    },
    serverInfo.settings.ALLOW_RESERVE_MANAGEMENT
      ? {
          key: 'reservable',
          behavior: BubbleTableFieldBehavior.MinWidth,
          thProps: { className: 'text-center' },
          tdProps: { className: 'text-center' },
          _toggleable: true,
        }
      : undefined,
    {
      key: 'createdAt',
      label: 'Created',
      behavior: BubbleTableFieldBehavior.Condense,
      thProps: {
        onClick: () => {
          const sortDirection =
            paginationParams.sortBy === 'createdAt'
              ? paginationParams.sortDirection * -1
              : SortOrder.DESC;
          setPaginationParams({ ...paginationParams, page: 1, sortBy: 'createdAt', sortDirection });
        },
      },
      render: () => (
        <span className="cursor-pointer">
          Created
          {paginationParams.sortBy === 'createdAt' && (
            <FontAwesomeIcon
              icon={getSortingIcon(paginationParams.sortDirection)}
              fixedWidth
              size="sm"
              className="cursor-pointer"
            />
          )}
        </span>
      ),
      _toggleable: true,
    },
    {
      key: 'actions',
      behavior: BubbleTableFieldBehavior.Condense,
      label: '',
      _toggleable: false,
    },
  ].filter(notUndefined);

  const tableTemplates: Array<BubbleTableTemplate<EventTableItem>> = [
    {
      key: 'title',
      render: item => {
        return (
          <>
            <div>{item.title}</div>
            {item.id.length > 0 && (
              <CopyToClipboardButton copyText={item._source._id} label="Copy ID" />
            )}
          </>
        );
      },
    },
    {
      key: 'access',
      render: item => {
        return (
          <Tooltip text="Click to manage access">
            <AccessGlanceButton
              item={item._source}
              onClick={() => {
                setShowAccessForm(bare(item._source));
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      key: 'segment',
      render: item => {
        return (
          item.segment && (
            <BubbleBadge key={item.segment._id} color={item.segment.color} className="mt-1 mr-1">
              {item.segment.name}
            </BubbleBadge>
          )
        );
      },
    },
    {
      key: 'levels',
      render: item => {
        return (
          item.levels.length > 0 &&
          item.levels.map(l => {
            return (
              <BubbleBadge key={l._id} color={hashStringToColor(l.name)} className="mr-1">
                {l.name}
              </BubbleBadge>
            );
          })
        );
      },
    },
    {
      key: 'attributes',
      render: item => {
        return (
          item.attributes.length > 0 &&
          item.attributes.map(a => (
            <BubbleBadge key={a._id} color={hashStringToColor(a.name)} className="mr-1">
              {a.name}
            </BubbleBadge>
          ))
        );
      },
    },
    {
      key: 'endpoints',
      render: item => <McmEndpoints template={item._source} />,
    },
    {
      key: 'private',
      render: item => {
        return item.isPrivate && <FontAwesomeIcon className="text-navy" icon="p" />;
      },
    },
    {
      key: 'crew',
      render: item => {
        return item.crewOnly && <FontAwesomeIcon className="text-navy" icon="c" />;
      },
    },
    {
      key: 'simple',
      render: item => {
        return item._source.simple && <FontAwesomeIcon className="text-navy" icon="s" />;
      },
    },
    {
      key: 'inventoried',
      render: item => {
        return item.inventoried && <FontAwesomeIcon className="text-navy" icon="i" />;
      },
    },
    {
      key: 'reservable',
      render: item => {
        return item.isReservable && <FontAwesomeIcon className="text-navy" icon="ticket" />;
      },
    },
    {
      key: 'createdAt',
      render: item => {
        let element: React.ReactNode = null;

        if (item.createdAt) {
          const createdAt = getLocaleDateTimeStringUTC(new Date(item.createdAt));
          const [date, time] = createdAt.split(', ');
          element = (
            <>
              <div className="block whitespace-nowrap">{date}</div>
              <div className="block whitespace-nowrap">{time}</div>
            </>
          );
        }

        return element;
      },
    },
    {
      key: 'actions',
      render: item => (
        <>
          <BubbleButton
            className="p-2-i"
            variant="navy"
            inverse
            icon="edit"
            onClick={() =>
              setMode({
                type: 'edit',
                edit: bare(item._source),
                staffTemplates: dataBundle?.staffTemplates ?? [],
              })
            }
          />
          {canManageLibraries(user) && (
            <BubbleButton
              className="p-2-i"
              variant="slate"
              inverse
              icon="copy"
              onClick={() =>
                setMode({
                  type: 'create',
                  edit: bare(item._source),
                  staffTemplates: dataBundle?.staffTemplates ?? [],
                })
              }
            />
          )}
          {canModifyMultiPropertyItem(user, serverInfo, property._id, item._source) && (
            <>
              {!showDelete[item.id] && (
                <BubbleButton
                  className="p-2-i"
                  variant="dirt"
                  inverse
                  onClick={() => {
                    setShowDelete({ ...showDelete, [item.id]: true });
                  }}
                  icon="trash-alt"
                />
              )}
              {showDelete[item.id] && (
                <>
                  <BubbleButton
                    className="p-2-i"
                    variant="dirt"
                    inverse
                    onClick={() => handleArchive(item._source, !item._source.archived)}
                    icon="trash-check"
                  />
                  <BubbleButton
                    className="p-2-i"
                    variant="slate"
                    inverse
                    onClick={() => {
                      setShowDelete({ ...showDelete, [item.id]: false });
                    }}
                    icon="trash-xmark"
                  />
                </>
              )}
            </>
          )}
        </>
      ),
    },
  ];

  const visibleTableFields = tableFields.filter(tf =>
    tf._visible !== undefined ? tf._visible : visibleFields[tf.key]
  );
  const toggleableTableFields = tableFields.filter(tf => tf._toggleable);

  function headerFn() {
    return <LibrariesSubNavigation active="events" />;
  }

  function bodyFn() {
    return (
      <BubbleOverlay loading={loading}>
        <BubbleTable
          responsive
          striped
          headVariant="titanium"
          fields={visibleTableFields}
          items={dataBundle?.tableItems}
          keyExtractor={item => item._source._id}
          stickyHeader={182}
          templates={tableTemplates}
          showPretitle
          pretitleClassName="p-0-i"
          showAllItems={true}
          onRowClick={itm => {
            setMode({
              type: 'edit',
              edit: bare(itm._source),
              staffTemplates: dataBundle?.staffTemplates ?? [],
            });
          }}
          pretitleRender={() => (
            <div className="px-4 pt-4 pb-1">
              <BubbleDebounce
                onTrigger={[
                  (search: string) => setPaginationParams({ ...paginationParams, page: 1, search }),
                  500,
                ]}
              >
                {({ debounce }) => (
                  <BubbleInput
                    type="text"
                    variant="titanium"
                    block
                    value={paginationParams.search}
                    inputClassName="outline-none-i"
                    onValue={debounce}
                    placeholder="Search..."
                  />
                )}
              </BubbleDebounce>
            </div>
          )}
        />

        {paginationMetadata && (
          <BubbleTablePaginationFooter
            paginationMetadata={paginationMetadata}
            perPage={paginationParams.perPage}
            onLoadMoreClick={() =>
              setPaginationParams({ ...paginationParams, page: paginationParams.page + 1 })
            }
            onPerPageChange={value =>
              setPaginationParams({ ...paginationParams, page: 1, perPage: value })
            }
          />
        )}
      </BubbleOverlay>
    );
  }

  function outerFn() {
    return (
      <>
        {mode && dataBundle && (
          <BubbleDrawer containerClassName="bg-eggshell" onClose={() => setMode(undefined)}>
            <BubbleDrawerBasicContainer
              title={
                mode.type === 'edit' ? `Edit Event - ${mode.edit?.title ?? ''}` : 'Create Event'
              }
              subtitle="Event Details"
            >
              <EventTemplateFormik
                mode={mode}
                onSubmit={handleSubmit}
                actionsAppearance="drawer-float"
                subnavClassName="-mx-8"
              />
            </BubbleDrawerBasicContainer>
          </BubbleDrawer>
        )}
        {showAccessForm && (
          <BubbleDrawer
            onClose={() => setShowAccessForm(undefined)}
            containerClassName="bg-eggshell"
          >
            <BubbleDrawerBasicContainer
              title={`Edit Event - ${showAccessForm?.title ?? ''}`}
              subtitle="Manage Access"
              onClose={() => setShowAccessForm(undefined)}
            >
              <PropertyAccessFormik
                edit={showAccessForm}
                makePropertyAccessItem={itm => ({ property: itm })}
                updateFn={update}
                actionsAppearance="drawer-float"
                subnavClassName="-mx-8"
                onSubmit={handleSubmit}
              />
            </BubbleDrawerBasicContainer>
          </BubbleDrawer>
        )}
      </>
    );
  }

  function sideFn() {
    return (
      <>
        <div className="flex flex-col relative items-stretch p-4 bg-titanium-darker">
          <div className="grid gap-2">
            {canManageLibraries(user) && (
              <BubbleButton
                variant="lime"
                onClick={() =>
                  setMode({ type: 'create', staffTemplates: dataBundle?.staffTemplates ?? [] })
                }
                className="text-left"
                icon="sparkles"
              >
                Create Event
              </BubbleButton>
            )}

            <BubbleButton
              variant="slate"
              onClick={generateCSV}
              className="text-left"
              icon="file-export"
            >
              Export to CSV
            </BubbleButton>
          </div>
        </div>

        <BubbleAccordion
          variant="titanium"
          className="flex-grow flex flex-col border-t border-forest-darkest"
          labels={['Filters']}
          open={[0]}
          locked={[0]}
        >
          <div className="grid gap-4 p-4">
            <BubbleFormGroup label="Dates" labelClassName="text-white-i">
              <div className="grid grid-cols-1 gap-1">
                <BubbleDebounce
                  onTrigger={[
                    (date: string) => {
                      setPaginationParams({
                        ...paginationParams,
                        page: 1,
                        startDate: date,
                      });
                    },
                    500,
                  ]}
                >
                  {({ debounce }) => (
                    <BubbleInput
                      variant="titanium"
                      type="date"
                      value={paginationParams.startDate}
                      onValue={debounce}
                    />
                  )}
                </BubbleDebounce>
                <BubbleDebounce
                  onTrigger={[
                    (date: string) =>
                      setPaginationParams({ ...paginationParams, page: 1, endDate: date }),
                    500,
                  ]}
                >
                  {({ debounce }) => (
                    <BubbleInput
                      variant="titanium"
                      type="date"
                      value={paginationParams.endDate}
                      onValue={debounce}
                    />
                  )}
                </BubbleDebounce>
              </div>
            </BubbleFormGroup>

            <BubbleFormGroup label="Options" labelClassName="text-white-i">
              <div className="grid gap-1">
                <BubbleCheckbox
                  variant="titanium"
                  className="text-white"
                  checked={paginationParams.archived}
                  onValue={(checked: boolean) => {
                    setPaginationParams({ ...paginationParams, page: 1, archived: checked });
                  }}
                  label="View Archived"
                />
              </div>
            </BubbleFormGroup>

            <BubbleFormGroup label="Fields" labelClassName="text-white-i">
              <div className="grid grid-cols-1 gap-1">
                {toggleableTableFields.map(tf => (
                  <BubbleCheckbox
                    key={tf.key}
                    variant="titanium"
                    className="text-white"
                    checked={visibleFields[tf.key]}
                    onValue={chk => setVisibleFields({ ...visibleFields, [tf.key]: chk })}
                    label={tf.label || camelToSentenceCase(tf.key)}
                  />
                ))}
              </div>
            </BubbleFormGroup>
          </div>
        </BubbleAccordion>
      </>
    );
  }

  return (
    <Libraries
      user={user}
      active="libraries"
      property={property}
      loading={loading}
      contentRef={contentRef}
      headerFn={headerFn}
      bodyFn={bodyFn}
      outerFn={outerFn}
      sideFn={sideFn}
      {...routeProps}
    />
  );
}
